import React, { useState, useRef, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import ReCAPTCHA from 'react-google-recaptcha';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { useResponsive, getFocusClasses } from '@/lib/responsive-utils';
import { sendContactEmail, initEmailJS, isEmailJSConfigured } from '@/lib/emailService';
import { trackFormSubmit } from '@/lib/analytics';
import type { ContactFormData } from '@/types';

// Validation schema
const contactFormSchema = z.object({
  name: z.string()
    .min(2, 'El nombre debe tener al menos 2 caracteres')
    .max(100, 'El nombre no puede exceder 100 caracteres')
    .regex(/^[a-zA-ZáéíóúÁÉÍÓÚñÑ\s]+$/, 'El nombre solo puede contener letras y espacios'),
  
  phone: z.string()
    .min(10, 'El teléfono debe tener al menos 10 dígitos')
    .max(15, 'El teléfono no puede exceder 15 dígitos')
    .regex(/^[\d\s\-\+\(\)]+$/, 'Formato de teléfono inválido'),
  
  email: z.string()
    .email('Ingrese un email válido')
    .max(255, 'El email no puede exceder 255 caracteres'),
  
  message: z.string()
    .min(10, 'El mensaje debe tener al menos 10 caracteres')
    .max(1000, 'El mensaje no puede exceder 1000 caracteres'),
  
  acceptTerms: z.boolean()
    .refine(val => val === true, 'Debe aceptar los términos y condiciones'),
});

interface ContactFormProps {
  onSubmit?: (data: ContactFormData) => Promise<void>;
  className?: string;
  recaptchaSiteKey?: string;
}

export const ContactForm: React.FC<ContactFormProps> = ({
  onSubmit,
  className = '',
  recaptchaSiteKey = '6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI', // Test key - replace with your real key
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const { isMobile } = useResponsive();

  // Initialize EmailJS on component mount
  useEffect(() => {
    initEmailJS();
  }, []);

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    reset,
    watch,
  } = useForm<ContactFormData>({
    resolver: zodResolver(contactFormSchema),
    mode: 'onChange',
    defaultValues: {
      name: '',
      phone: '',
      email: '',
      message: '',
      acceptTerms: false,
    },
  });

  // Watch form values for real-time validation feedback
  const watchedValues = watch();

  const handleFormSubmit = async (data: ContactFormData) => {
    if (!recaptchaToken) {
      setSubmitStatus('error');
      setErrorMessage('Por favor, completa la verificación reCAPTCHA.');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');
    setErrorMessage('');

    try {
      if (onSubmit) {
        // Use custom submit handler if provided
        await onSubmit(data);
      } else {
        // Check if EmailJS is configured
        if (!isEmailJSConfigured()) {
          throw new Error('El servicio de correo no está configurado. Por favor, contacta al administrador.');
        }
        
        // Send email using EmailJS
        await sendContactEmail(data, recaptchaToken);
      }
      
      setSubmitStatus('success');
      trackFormSubmit('contact_form'); // Track successful form submission
      reset();
      setRecaptchaToken(null);
      recaptchaRef.current?.reset();
      
      // Scroll to success message
      setTimeout(() => {
        const successElement = document.getElementById('form-success-message');
        successElement?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 100);
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
      setErrorMessage(
        error instanceof Error 
          ? error.message 
          : 'No se pudo enviar el mensaje. Por favor, intenta nuevamente.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRecaptchaChange = (token: string | null) => {
    setRecaptchaToken(token);
  };

  return (
    <section className={`py-8 sm:py-12 lg:py-16 ${className}`}>
      <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-6 sm:mb-8 lg:mb-12">
           {/* Badge */}
            <div className="inline-flex items-center gap-2 text-liec-primary-500 font-semibold text-sm uppercase tracking-wider">
              Contáctanos
            
            
           <svg className='inline-flex items-center gap-2 ml-2' width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
<line x1="0.330774" y1="5.41623" x2="11.9289" y2="11.1156" stroke="#E34C0E" stroke-width="1.5"/>
<path d="M6.36253 13.292L12.4163 11.2088L10.5026 4.91892" stroke="#E34C0E" stroke-width="1.5"/>
</svg>

</div>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-3 sm:mb-4">
            ¿Listo para iniciar tu proyecto?
          </h2>
          <p className="text-sm sm:text-base lg:text-lg text-gray-600 max-w-2xl mx-auto px-4">
            Solicita tu cotización personalizada y asegura calidad con LIEC.
          </p>
        </div>

        <div className="max-w-2xl mx-auto">
          <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4 sm:space-y-6">
            {/* Name Field */}
            <div>
              <Label htmlFor="name" className="text-sm font-medium text-gray-700 block mb-2">
                Nombre Completo *
              </Label>
              <Input
                id="name"
                type="text"
                {...register('name')}
                className={`${errors.name ? 'border-red-500 focus:ring-red-500' : ''} ${getFocusClasses()}`}
                placeholder="Ingrese su nombre completo"
                aria-invalid={errors.name ? 'true' : 'false'}
                aria-describedby={errors.name ? 'name-error' : undefined}
              />
              {errors.name && (
                <p id="name-error" className="mt-2 text-xs sm:text-sm text-red-600 flex items-start">
                  <AlertCircle className="w-4 h-4 mr-1 flex-shrink-0 mt-0.5" />
                  <span>{errors.name.message}</span>
                </p>
              )}
            </div>

            {/* Phone and Email Fields - Responsive Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
              {/* Phone Field */}
              <div>
                <Label htmlFor="phone" className="text-sm font-medium text-gray-700 block mb-2">
                  Teléfono *
                </Label>
                <Input
                  id="phone"
                  type="tel"
                  {...register('phone')}
                  className={`${errors.phone ? 'border-red-500 focus:ring-red-500' : ''} ${getFocusClasses()}`}
                  placeholder="55 1234 5678"
                  aria-invalid={errors.phone ? 'true' : 'false'}
                  aria-describedby={errors.phone ? 'phone-error' : undefined}
                />
                {errors.phone && (
                  <p id="phone-error" className="mt-2 text-xs sm:text-sm text-red-600 flex items-start">
                    <AlertCircle className="w-4 h-4 mr-1 flex-shrink-0 mt-0.5" />
                    <span>{errors.phone.message}</span>
                  </p>
                )}
              </div>

              {/* Email Field */}
              <div>
                <Label htmlFor="email" className="text-sm font-medium text-gray-700 block mb-2">
                  Correo Electrónico *
                </Label>
                <Input
                  id="email"
                  type="email"
                  {...register('email')}
                  className={`${errors.email ? 'border-red-500 focus:ring-red-500' : ''} ${getFocusClasses()}`}
                  placeholder="ejemplo@correo.com"
                  aria-invalid={errors.email ? 'true' : 'false'}
                  aria-describedby={errors.email ? 'email-error' : undefined}
                />
                {errors.email && (
                  <p id="email-error" className="mt-2 text-xs sm:text-sm text-red-600 flex items-start">
                    <AlertCircle className="w-4 h-4 mr-1 flex-shrink-0 mt-0.5" />
                    <span>{errors.email.message}</span>
                  </p>
                )}
              </div>
            </div>

            {/* Message Field */}
            <div>
              <Label htmlFor="message" className="text-sm font-medium text-gray-700 block mb-2">
                Mensaje *
              </Label>
              <Textarea
                id="message"
                {...register('message')}
                rows={isMobile ? 4 : 5}
                className={`${errors.message ? 'border-red-500 focus:ring-red-500' : ''} ${getFocusClasses()} resize-none`}
                placeholder="Describa su proyecto y los servicios que necesita..."
                aria-invalid={errors.message ? 'true' : 'false'}
                aria-describedby={errors.message ? 'message-error' : undefined}
              />
              {errors.message && (
                <p id="message-error" className="mt-2 text-xs sm:text-sm text-red-600 flex items-start">
                  <AlertCircle className="w-4 h-4 mr-1 flex-shrink-0 mt-0.5" />
                  <span>{errors.message.message}</span>
                </p>
              )}
              <p className="mt-2 text-xs sm:text-sm text-gray-500 text-right">
                {watchedValues.message?.length || 0}/1000 caracteres
              </p>
            </div>

            {/* reCAPTCHA */}
            <div className="flex justify-center sm:justify-start">
              <div className="transform scale-90 sm:scale-100 origin-left">
                <ReCAPTCHA
                  ref={recaptchaRef}
                  sitekey={recaptchaSiteKey}
                  onChange={handleRecaptchaChange}
                  onExpired={() => setRecaptchaToken(null)}
                  size={isMobile ? 'compact' : 'normal'}
                />
              </div>
            </div>

            {/* Terms and Conditions */}
            <div className="flex items-start space-x-2 sm:space-x-3">
              <input
                id="acceptTerms"
                type="checkbox"
                {...register('acceptTerms')}
                className={`mt-1 w-4 h-4 text-liec-primary-500 border-gray-300 rounded focus:ring-liec-primary-500 flex-shrink-0 ${
                  errors.acceptTerms ? 'border-red-500' : ''
                }`}
                aria-invalid={errors.acceptTerms ? 'true' : 'false'}
                aria-describedby={errors.acceptTerms ? 'terms-error' : undefined}
              />
              <div className="flex-1">
                <Label htmlFor="acceptTerms" className="text-xs sm:text-sm text-gray-700 cursor-pointer leading-relaxed">
                  Acepto los{' '}
                  <a href="#terms" className="text-liec-primary-500 hover:text-liec-primary-600 underline">
                    términos y condiciones
                  </a>{' '}
                  y la{' '}
                  <a href="#privacy" className="text-liec-primary-500 hover:text-liec-primary-600 underline">
                    política de privacidad
                  </a>
                  . *
                </Label>
                {errors.acceptTerms && (
                  <p id="terms-error" className="mt-1 text-xs sm:text-sm text-red-600 flex items-start">
                    <AlertCircle className="w-4 h-4 mr-1 flex-shrink-0" />
                    {errors.acceptTerms.message}
                  </p>
                )}
              </div>
            </div>

            {/* Submit Button */}
            <div className="pt-2 sm:pt-4">
              <Button
                type="submit"
                disabled={!isValid || !recaptchaToken || isSubmitting}
                className={`w-full bg-liec-primary-500 hover:bg-liec-primary-600 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${getFocusClasses()} text-sm sm:text-base`}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Enviando...
                  </>
                ) : (
                  'Enviar Solicitud'
                )}
              </Button>
            </div>

            {/* Status Messages */}
            {submitStatus === 'success' && (
              <div id="form-success-message" className="mt-4 p-3 sm:p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-green-800 font-medium text-sm sm:text-base">
                      ¡Mensaje enviado exitosamente!
                    </p>
                    <p className="text-green-700 text-xs sm:text-sm mt-1">
                      Hemos recibido tu solicitud. Nos pondremos en contacto contigo en las próximas 24 horas.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="mt-4 p-3 sm:p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-start">
                  <AlertCircle className="w-5 h-5 text-red-600 mr-2 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-red-800 font-medium text-sm sm:text-base">
                      Error al enviar el mensaje
                    </p>
                    <p className="text-red-700 text-xs sm:text-sm mt-1">
                      {errorMessage || 'Por favor, verifica que todos los campos estén completos y el reCAPTCHA esté marcado.'}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </form>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;
